import type { FeederFieldConfig, KkpField } from '@kkp-fraud/shared';

/**
 * The 32 raw feeder fields KKP sends to pre-detection, row for row from KKP's
 * "Fields for Pre-Detection.xlsx" (delivered 23 Sep 2026).
 *
 * This is the RAW payload — what arrives on the wire — not the normalized fields a rule
 * reads (`fields.ts`). `sends` is the sheet's L / S / M columns. A `-` in the DE column
 * means KKP's host supplies the value for every transaction (it is not an ISO 8583
 * element), so all three sources send it.
 *
 * This list is the DEFAULT. The console edits the feeder payload into the draft as
 * `RuleSet.feederFields` (Field Management → Feeder payload → Edit) and releases it through
 * the same propose/approve path as a rule; `effectiveFeederConfig` picks the draft's copy
 * when it has one. The source registry (`sources.ts`) derives from whichever is in effect.
 *
 * Row 28 (DE 60) was the one key KKP named only by display name; `additional_pos_info` is
 * now a `KkpField` in `packages/shared` (the engine reads its characters 9–10 as the ECI
 * when DE 48.42.1 is absent), so it is no longer an assumed key. `keyAssumed` stays in the
 * config shape for a row an analyst adds before KKP confirms its key.
 */

export type FeederKey = KkpField;

/** A built-in row: the shared config shape, with the key narrowed to the keys KKP named. */
export type FeederField = FeederFieldConfig & { key: FeederKey };

/**
 * The status column of KKP's sheet, spelled as the sheet spells it. A code the bank's own
 * document uses, printed verbatim in both languages — not prose to translate.
 */
export const FEEDER_STATUSES: readonly FeederFieldConfig['status'][] = ['AS-IS', 'New'];

/** What the console reads the feeder payload from — the draft's copy or the built-in list. */
export type FeederConfig = readonly FeederFieldConfig[];

const ALL = { ITMX_LOCAL: true, ITMX_SINGLE: true, MASTERCARD: true } as const;
const MC = { ITMX_LOCAL: false, ITMX_SINGLE: false, MASTERCARD: true } as const;
const LOCAL_MC = { ITMX_LOCAL: true, ITMX_SINGLE: false, MASTERCARD: true } as const;
const SINGLE_MC = { ITMX_LOCAL: false, ITMX_SINGLE: true, MASTERCARD: true } as const;
const LOCAL = { ITMX_LOCAL: true, ITMX_SINGLE: false, MASTERCARD: false } as const;

export const FEEDER_FIELDS: readonly FeederField[] = [
  { no: 1, de: null, name: 'Customer No', key: 'customer_no', status: 'AS-IS', sends: ALL },
  { no: 2, de: null, name: 'Card Ref No', key: 'reference_card_no', status: 'AS-IS', sends: ALL },
  { no: 3, de: null, name: 'Transaction ID', key: 'transaction_id', status: 'AS-IS', sends: ALL },
  { no: 4, de: null, name: 'Message Type', key: 'message_type', status: 'AS-IS', sends: ALL },
  { no: 5, de: null, name: 'Card Product Name', key: 'card_product_name', status: 'AS-IS', sends: ALL },
  { no: 6, de: null, name: 'Switching Type', key: 'switching_name', status: 'New', sends: ALL },
  { no: 7, de: 'DE 3', name: 'Processing Code', key: 'processing_code', status: 'AS-IS', sends: ALL },
  { no: 8, de: 'DE 4', name: 'Transaction Amount', key: 'transaction_amount', status: 'New', sends: ALL },
  { no: 9, de: 'DE 6', name: 'Cardholder Billing Amount', key: 'cardholder_billing_amount', status: 'New', sends: LOCAL_MC },
  { no: 10, de: 'DE 7', name: 'Transaction Datetime', key: 'transaction_datetime', status: 'AS-IS', sends: ALL },
  { no: 11, de: 'DE 12', name: 'Local Transaction Time', key: 'local_transaction_time', status: 'New', sends: SINGLE_MC },
  { no: 12, de: 'DE 13', name: 'Local Transaction Date', key: 'local_transaction_date', status: 'New', sends: SINGLE_MC },
  { no: 13, de: 'DE 18', name: 'Merchant Type (MCC)', key: 'merchant_type', status: 'New', sends: ALL },
  { no: 14, de: 'DE 19', name: 'Acquiring Institution Country Code', key: 'acquiring_inst_country_code', status: 'New', sends: LOCAL_MC },
  { no: 15, de: 'DE 22', name: 'POS Entry Mode', key: 'pos_entry_mode', status: 'AS-IS', sends: ALL },
  { no: 16, de: 'DE 22.1', name: 'POS Terminal PAN Entry Mode', key: 'pos_term_pan_entry_mode', status: 'New', sends: MC },
  { no: 17, de: 'DE 22.2', name: 'POS Terminal PIN Entry Mode', key: 'pos_term_pin_entry_mode', status: 'New', sends: MC },
  { no: 18, de: 'DE 41', name: 'Card Acceptor Term ID', key: 'card_acceptor_term_id', status: 'AS-IS', sends: ALL },
  { no: 19, de: 'DE 42', name: 'Card Acceptor ID', key: 'card_acceptor_id', status: 'AS-IS', sends: ALL },
  { no: 20, de: 'DE 43', name: 'Card Acceptor Name and Location', key: 'card_acceptor_name', status: 'AS-IS', sends: ALL },
  { no: 21, de: 'DE 43.1', name: 'Acceptor Name', key: 'acceptor_name', status: 'New', sends: MC },
  { no: 22, de: 'DE 43.3', name: 'Acceptor City', key: 'acceptor_city', status: 'New', sends: MC },
  { no: 23, de: 'DE 43.5', name: 'Card Acceptor Country Code', key: 'card_acceptor_country_code', status: 'AS-IS', sends: MC },
  { no: 24, de: 'DE 48.42.1', name: 'E-commerce Security Level / UCAF Indicator', key: 'electronic_commerce', status: 'New', sends: MC },
  { no: 25, de: 'DE 48.75.01', name: 'Fraud Score', key: 'fraud_score', status: 'New', sends: MC },
  { no: 26, de: 'DE 48.75.02', name: 'Fraud Score Reason Code', key: 'fraud_score_reason_code', status: 'New', sends: MC },
  { no: 27, de: 'DE 49', name: 'Transaction Currency Code', key: 'currency_code', status: 'New', sends: ALL },
  {
    no: 28,
    de: 'DE 60',
    name: 'Additional POS Information',
    key: 'additional_pos_info',
    status: 'New',
    sends: LOCAL,
    note: 'ITMX Local — digit 9–10 = 48.42.1 (ECI)',
  },
  { no: 29, de: 'DE 61.01', name: 'POS Terminal Attendance', key: 'pos_term_attendence', status: 'New', sends: MC },
  { no: 30, de: 'DE 61.07', name: 'POS Transaction Status', key: 'pos_tran_status', status: 'New', sends: MC },
  { no: 31, de: 'DE 61.10', name: 'Cardholder Activated Terminal Level', key: 'cardholder_act_term_level', status: 'New', sends: MC },
  { no: 32, de: 'DE 61.11', name: 'POS Card Data Terminal Input Capability', key: 'pos_card_indicator', status: 'New', sends: MC },
];

/** The feeder payload in force for a document: its own copy when it carries one, else the default. */
export function effectiveFeederConfig(rs: { feederFields?: FeederFieldConfig[] } | null | undefined): FeederConfig {
  return rs?.feederFields ?? FEEDER_FIELDS;
}
